import { useCallback } from 'react';
import uniqBy from 'lodash/uniqBy';

import { useTranslation } from '@lyearn/i18n';
import { Popper, PopperProps, Select, Typography } from '@lyearn/ui';
import {
  correctOptionItemBackground,
  incorrectOptionItemBackground,
} from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem/styles';
import { OptionsListItemTypeEnum } from '@/modules/corporate/modules/questions/fields/single-select/types';

import { getFtbResponseType } from '../../../../../utils';
import BlankBox from '../BlankBox';
import { dropdownStyles, selectInputStyles, selectOptionLabelStyles } from './styles';
import { FtbBlankProps } from './types';

const POPPER_STYLE = { width: 'fit-content' };

const PopperComponent = (props: PopperProps) => {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      style={{ ...POPPER_STYLE, minWidth: props.style?.width }}
    />
  );
};

const FtbBlank: React.FC<FtbBlankProps> = (props) => {
  const {
    isQuestionSubmitted,
    ftbResponse,
    ftbComponent,
    onChange,
    correctFtbAnswers,
    globalDistractedOptions,
  } = props;

  const { t } = useTranslation();

  const type = getFtbResponseType({ ftbResponse, ftbComponent, correctFtbAnswers });

  const submittedBlankClassName =
    type === OptionsListItemTypeEnum.Correct
      ? correctOptionItemBackground
      : incorrectOptionItemBackground;

  const response =
    ftbResponse.find((blankResponse) => blankResponse.blankId === ftbComponent.id)?.text || '';

  const onChangeValue = useCallback(
    (blankId: string, text: string) => {
      ftbResponse.unshift({ blankId, text });
      const updatedFtbResponse = uniqBy(ftbResponse, 'blankId');
      onChange(updatedFtbResponse);
    },
    [ftbResponse, onChange],
  );

  const options = globalDistractedOptions.map((dist) => ({ value: dist.text }));

  if (isQuestionSubmitted) {
    return <BlankBox className={submittedBlankClassName} color="text-text-white" text={response} />;
  }

  return (
    <Select
      disableClearable
      PopperComponent={PopperComponent}
      className="inline-block me-16"
      classes={{ inputRoot: dropdownStyles(response.length), input: selectInputStyles }}
      getOptionLabel={(option) => option.value}
      optionLabelRenderer={(option) => (
        <Typography
          className={selectOptionLabelStyles}
          color="text-text-secondary"
          variant="heading-m">
          {option.value}
        </Typography>
      )}
      options={options}
      placeholder={t('Common:Actions.Select')}
      value={response}
      onChange={(value) => onChangeValue(ftbComponent.id, value)}
    />
  );
};

export default FtbBlank;
